// pages/product/Product.jsx
import { useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiArrowRight } from "react-icons/fi";
import { useSEO } from "../../hooks/useSEO";
import { VARIANT_ROUTE } from "../../routes/constant";
import {
  fetchProductDetails,
  clearProductData,
  setSelectedVariant,
} from "../../store/slices/productSlice";
import { useTheme } from "../../theme/ThemeProvider";
import Layout from "../../components/Layout";
import ProductImage from "../../components/product/ProductImage";
import ProductInfo from "../../components/product/ProductInfo";
import ProductVariants from "../../components/product/ProductVariants";
import ProductPriceCard from "../../components/product/ProductPriceCard";
import ProductFeatures from "../../components/product/ProductFeatures";
import ProductSkeleton from "../../components/product/ProductSkeleton";
import ErrorState from "../../components/state/ErrorState";
import EmptyState from "../../components/state/EmptyState";

const Product = () => {
  const dispatch = useDispatch();
  const { categorySlug, brandSlug, productSlug } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const {
    product,
    brand,
    category: categoryData,
    variations,
    loading,
    error,
    selectedVariant,
  } = useSelector((state) => state.product) || {
    product: null,
    brand: null,
    category: null,
    variations: [],
    loading: false,
    error: null,
    selectedVariant: null,
  };

  const { selectedCity } = useSelector((state) => state.city) || {
    selectedCity: null,
  };
  const cityId = selectedCity?.id;

  // Fetch product data
  useEffect(() => {
    if (productSlug && brandSlug && categorySlug) {
      dispatch(clearProductData());
      dispatch(fetchProductDetails({
        categorySlug,
        brandSlug,
        productSlug
      }));
    }

    return () => {
      dispatch(clearProductData());
    };
  }, [dispatch, categorySlug, brandSlug, productSlug, cityId]);

  // Auto-select first variant when variations load
  useEffect(() => {
    if (variations.length > 0 && !selectedVariant) {
      dispatch(setSelectedVariant(variations[0]));
    }
  }, [variations, selectedVariant, dispatch]);

  const productPrice = useMemo(() => {
    if (selectedVariant?.price) return selectedVariant.price;
    if (product?.price) return product.price;
    if (variations?.length > 0) {
      const pricedVariant = variations.find((v) => v.price);
      return pricedVariant?.price || 0;
    }
    return 0;
  }, [selectedVariant, product, variations]);

  const productTitle = product?.title || productSlug?.replace(/-/g, " ") || "";
  const variationsCount = variations?.length || 0;
  const brandName = brand?.name || "";
  const categoryName = categoryData?.name || "";

  // SEO
  const seoParams = useMemo(() => {
    if (loading || error || !product) return null;

    let maxPrice = 0;
    if (variations?.length > 0) {
      const prices = variations.map((v) => v.price).filter((p) => p > 0);
      if (prices.length > 0) {
        maxPrice = Math.max(...prices);
      }
    }
    if (productPrice > maxPrice) maxPrice = productPrice;

    return {
      categorySlug: categorySlug,
      brandSlug: brandSlug,
      productSlug: productSlug,
      categoryName: categoryData?.name || "",
      brandName: brand?.name || "",
      productName: product?.title || "",
      productDescription: product?.description || "",
      maxPrice: maxPrice,
      variantsCount: variations?.length || 0,
      selectedVariant: selectedVariant?.name || "",
    };
  }, [
    loading,
    error,
    product,
    variations,
    productTitle,
    categoryData,
    brand,
    categorySlug,
    brandSlug,
    productSlug,
    variationsCount,
    productPrice,
    selectedVariant,
  ]);

  useSEO(
    `/sell-used-online/${categorySlug}/${brandSlug}/${productSlug}`,
    seoParams,
  );

  const handleVariantSelect = (variant) => {
    dispatch(setSelectedVariant(variant));
    // Navigate directly to variant page when variant is selected
    navigate(VARIANT_ROUTE(categorySlug, brandSlug, productSlug, variant.slug));
  };

  const handleGetExactValue = () => {
    // This won't be called as button is disabled, but kept for completeness
    toast.error("Please select a variant first");
  };

  const renderContent = () => {
    if (loading) {
      return <ProductSkeleton isDarkMode={isDarkMode} />;
    }

    if (error) {
      return (
        <ErrorState
          error={error}
          onRetry={() =>
            dispatch(
              fetchProductDetails({
                categorySlug,
                brandSlug,
                productSlug,
              }),
            )
          }
          isDarkMode={isDarkMode}
        />
      );
    }

    if (!product) {
      return (
        <EmptyState
          isDarkMode={isDarkMode}
          title="Product not found"
          message="The product you're looking for doesn't exist or has been removed."
        />
      );
    }

    const mainImage = product.image;

    return (
      <div
        className={`grid grid-cols-1 lg:grid-cols-2 gap-2 md:gap-6 p-4 lg:p-6 rounded-2xl ${
          isDarkMode ? "bg-gray-800" : "bg-white shadow-sm"
        }`}
      >
        <div className="grid grid-cols-3 gap-3 md:hidden">
          <ProductImage
            image={mainImage}
            title={product.title}
            isDarkMode={isDarkMode}
          />
          <div className="col-span-2">
            <ProductInfo title={product.title} isDarkMode={isDarkMode} />
          </div>
        </div>

        <div className="items-center justify-center hidden md:flex">
          <ProductImage
            image={mainImage}
            title={product.title}
            isDarkMode={isDarkMode}
          />
        </div>

        <div className="space-y-4">
          <div className="hidden md:block">
            <ProductInfo title={product.title} isDarkMode={isDarkMode} />
          </div>

          <ProductPriceCard
            productPrice={productPrice}
            isDarkMode={isDarkMode}
            selectedVariant={selectedVariant}
          />

          {variations.length > 0 && (
            <ProductVariants
              variants={variations}
              onVariantSelect={handleVariantSelect}
              isDarkMode={isDarkMode}
              variantType="card"
            />
          )}

          {/* Disabled Get Exact Value Button */}
          <button
            onClick={handleGetExactValue}
            disabled={true}
            className={`w-full py-3 md:py-4 text-sm md:text-base font-medium rounded-xl flex items-center justify-center gap-2 transition-colors cursor-not-allowed ${
              isDarkMode
                ? "bg-gray-600 text-gray-400"
                : "bg-gray-300 text-gray-500"
            }`}
          >
            <span>Get Exact Value</span>
            <FiArrowRight size={16} />
          </button>

          <ProductFeatures isDarkMode={isDarkMode} />
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="container px-4 py-6 mx-auto max-w-7xl">
        {renderContent()}
      </div>
    </Layout>
  );
};

export default Product;
